import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Avatar,
  Container,
  Typography,
  Card,
  Stack,
  Button,
  Grid,
  
  CircularProgress,
  Chip, 
  Accordion,
  AccordionSummary, 
  AccordionDetails,
  Paper,
  IconButton,
Tooltip,
  useMediaQuery,
  Fab,
  Zoom,
  Skeleton,
} from "@mui/material";

import { Seo } from "src/components/seo";
import { customersApi } from "src/api/endpoints/customers";
import type { Customer, KycData, BankData } from "src/api/types/customer.types";
import { format, parseISO } from "date-fns";
import CreateKycModal from "../../components/customers/create-kyc-modal";
import CreateBankModal from "../../components/customers/create-bank-modal";
import CreateTransactionModal from "../../components/customers/create-transaction-modal";
import axios from "axios";
import { toast } from "react-hot-toast";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import config from "./../../../apiServices/appConfig";
import { styled, useTheme, alpha } from "@mui/material/styles";
import BackButton from 'src/components/back-button';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import UpdateIcon from '@mui/icons-material/Update';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import VerifiedIcon from '@mui/icons-material/Verified';
import PendingIcon from '@mui/icons-material/Pending';
import ErrorIcon from '@mui/icons-material/Error';
import AddIcon from '@mui/icons-material/Add';
import SecurityIcon from '@mui/icons-material/Security';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import type { ChipProps } from '@mui/material/Chip';

const BASE_URL = config.BASE_URL+config.API_VERSION;

// Add type for StatusChip props
interface StatusChipProps extends ChipProps {
  $status?: string;
}



// ===== STYLED COMPONENTS =====

// Profile Header Card with Gradient and Animation
const ProfileHeaderCard = styled(Card)(({ theme }) => ({
  // background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color:"black",
  left:"10%",
  // borderRadius: '20px',
  position: 'relative',

}));

// Enhanced Avatar with Status Indicator
const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  border: `4px solid ${theme.palette.common.white}`,
  boxShadow: theme.shadows[8],
  marginBottom: theme.spacing(2),
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 8,
    right: 8,
    width: 24,
    height: 24,
    backgroundColor: theme.palette.success.main,
    borderRadius: '50%',
    border: `3px solid ${theme.palette.common.white}`,
    boxShadow: theme.shadows[2],
  },
  [theme.breakpoints.down('sm')]: {
    width: 100,
    height: 100,
  },
}));

// Modern Info Card with Hover Effects
const InfoCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  padding: theme.spacing(3),
  height: '100%',
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  position: 'relative',
  overflow: 'hidden',
  
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    transform: 'scaleX(0)',
    transformOrigin: 'left',
  },
  
  
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

// Interactive Data Row with Smooth Animations
const DataRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 0),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)', 
  borderRadius: theme.spacing(1),
  position: 'relative',

  
  '&:last-child': {
    borderBottom: 'none',
  },
  
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '3px',
    background: theme.palette.primary.main,
    transform: 'scaleY(0)',
    transition: 'transform 0.2s ease',
  },
  
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    margin: theme.spacing(0, -1),
    padding: theme.spacing(1.5, 1),
    transform: 'translateX(8px)',
    
    '&::before': {
      transform: 'scaleY(1)',
    },
    
    '& .data-icon': {
      color: theme.palette.primary.main,
      transform: 'scale(1.2)',
    },
    
    '& .data-label': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
    
    '& .data-value': {
      fontWeight: 600,
    },
  },
  
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
  },
}));

// Enhanced Status Chip with Glow Effect
const StatusChip = styled(Chip)<StatusChipProps>(({ theme, $status }) => {
  const getStatusStyles = () => {
    switch ($status?.toLowerCase()) {
      case 'success':
        return {
          backgroundColor: alpha(theme.palette.success.main, 0.1),
          color: theme.palette.success.dark,
          border: `1px solid ${theme.palette.success.main}`,
          boxShadow: `0 0 20px ${alpha(theme.palette.success.main, 0.3)}`,
        };
      case 'pending':
        return {
          backgroundColor: alpha(theme.palette.warning.main, 0.1),
          color: theme.palette.warning.dark,
          border: `1px solid ${theme.palette.warning.main}`,
          boxShadow: `0 0 20px ${alpha(theme.palette.warning.main, 0.3)}`,
         
        };
      case 'error':
        return {
          backgroundColor: alpha(theme.palette.error.main, 0.1),
          color: theme.palette.error.dark,
          border: `1px solid ${theme.palette.error.main}`,
          boxShadow: `0 0 20px ${alpha(theme.palette.error.main, 0.3)}`,
        };
      default:
        return {
          backgroundColor: alpha(theme.palette.grey[500], 0.1),
          color: theme.palette.grey[700],
          border: `1px solid ${theme.palette.grey[400]}`,
        };
    }
  };

  return {
    fontWeight: 600,
    textTransform: 'uppercase',
    fontSize: '0.75rem',
    transition: 'all 0.3s ease',
    ...getStatusStyles(),
    
    '&:hover': {
      transform: 'scale(1.05)',
    },
  };
});

// Section Header with Gradient Underline
const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  position: 'relative',
  
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    borderRadius: '1px',
  },
  
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
}));

// Responsive Container
const ResponsiveContainer = styled(Container)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
}));

// Quick Stats Card
const QuickStatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '12px',
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  textAlign: 'center',
  transition: 'all 0.3s ease',

}));

// Animated Card Component
const AnimatedCard = styled(Card)(({ theme }) => ({
  borderRadius: '16px',
  padding: theme.spacing(3),
  height: '100%',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: theme.shadows[12],
    borderColor: theme.palette.primary.main,
    '& .card-icon': {
      transform: 'scale(1.1)',
      color: theme.palette.primary.main,
    },
  },
}));

// Progress Ring Component
const ProgressRing: React.FC<{ 
  progress: number; 
  size?: number; 
  strokeWidth?: number;
  color?: string;
}> = ({ 
  progress, 
  size = 120, 
  strokeWidth = 8,
  color = '#1976d2'
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <svg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(0, 0, 0, 0.1)"
          strokeWidth={strokeWidth}
          fill="transparent"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="transparent"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{
            transition: 'stroke-dashoffset 0.5s ease-in-out',
            transform: 'rotate(-90deg)',
            transformOrigin: '50% 50%',
          }}
        />
      </svg>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="h6" component="div" color="text.secondary" fontWeight="bold">
          {Math.round(progress)}%
        </Typography>
      </Box>
    </Box>
  );
};

// Enhanced Loading Skeleton Component
const ProfileSkeleton: React.FC = () => (
  <Box sx={{ p: 3 }}>
    <Card sx={{ p: 4, mb: 3, borderRadius: '20px' }}>
      <Stack direction="row" spacing={3} alignItems="center">
        <Skeleton variant="circular" width={120} height={120} />
        <Stack spacing={2} sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={40} />
          <Skeleton variant="text" width="80%" height={24} />
          <Skeleton variant="rectangular" width="40%" height={32} />
          <Skeleton variant="rectangular" width="100%" height={8} />
        </Stack>
      </Stack>
    </Card>
    
    <Stack direction="row" spacing={3}>
      {[1, 2, 3].map((item) => (
        <Card key={item} sx={{ p: 3, flex: 1, borderRadius: '16px' }}>
          <Skeleton variant="text" width="60%" height={32} />
          <Stack spacing={2} sx={{ mt: 2 }}>
            {[1, 2, 3, 4].map((row) => (
              <Box key={row} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Skeleton variant="circular" width={24} height={24} />
                <Stack spacing={0.5} sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="40%" height={16} />
                  <Skeleton variant="text" width="60%" height={20} />
                </Stack>
              </Box>
            ))}
          </Stack>
        </Card>
      ))}
    </Stack>
  </Box>
);

// Stats Card Component
const StatsCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
}> = ({ title, value, icon, color = 'primary', trend, trendValue }) => (
  <AnimatedCard>
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box 
          className="card-icon"
          sx={{ 
            color: `${color}.main`,
            backgroundColor: `${color}.light`,
            borderRadius: '12px',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        {trend && trendValue && (
          <Chip
            label={trendValue}
            size="small"
            color={trend === 'up' ? 'success' : trend === 'down' ? 'error' : 'default'}
            variant="outlined"
          />
        )}
      </Box>
      
      <Box>
        <Typography variant="h4" fontWeight="bold" color="text.primary">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
      </Box>
    </Stack>
  </AnimatedCard>
);

// Empty State Component
const EmptyState: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
}> = ({ icon, title, description, action }) => (
  <Box sx={{ 
    textAlign: 'center', 
    py: 6,
  }}>
    <Box sx={{ 
      color: 'text.disabled', 
      mb: 2,
      '& svg': { fontSize: 64 }
    }}>
      {icon}
    </Box>
    <Typography variant="h6" color="text.secondary" gutterBottom>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
      {description}
    </Typography>
    {action}
  </Box>
);

// ===== MAIN COMPONENT =====
const CustomerProfilePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { id } = useParams<{ id: string }>();
  
  // State management
  const [loading, setLoading] = useState<boolean>(true);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [kycData, setKycData] = useState<KycData[]>([]);
  const [bankData, setBankData] = useState<BankData[]>([]);
  const [kycModalOpen, setKycModalOpen] = useState(false);
  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [transactionModalOpen, setTransactionModalOpen] = useState(false);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState<boolean>(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Scroll to top functionality
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ===== FETCH FUNCTIONS =====
  const fetchKycData = async () => {
    if (!id) return;
    try {
      const response = await customersApi.getKycByUserId(id);
      if (response.status) {
        setKycData(response.data.response.data.kycData);
      }
    } catch (error) {
      console.error("Error fetching KYC data:", error);
    }
  };

  const fetchBankData = async () => {
    if (!id) return;
    try {
      const response = await customersApi.getBankByUserId(id);
      if (response.status) {
        setBankData(response.data);
      }
    } catch (error) {
      console.error("Error fetching bank data:", error);
    }
  };

  const fetchTransactions = async () => {
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
  
    if (!id) return;
  
    try {
      setTransactionsLoading(true);
      const response = await axios.get(`${BASE_URL}/transaction/${id}`, { headers });
  
      if (response.data.status) {
        setTransactions(response.data.data.total_transactions.rows);
      }
    } catch (error) {
      if (error?.response?.status === 403) {
        toast.error(error.response.data.message || 'Operation not permitted');
      } else {
        // toast.error('Failed to fetch transactions');
      }
    } finally {
      setTransactionsLoading(false);
    }
  };

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      try {
        setLoading(true);
        if (!id) throw new Error("Customer ID is required");
        
        const response = await customersApi.getCustomerById(id);
        if (response.status) {
          setCustomer(response.data);
        } else {
          console.error("Failed to fetch customer details");
        }
      } catch (error) {
        console.error("Error fetching customer details:", error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCustomerDetails();
    }
  }, [id]);

  useEffect(() => {
    fetchKycData();
    fetchBankData();
    fetchTransactions();
  }, [id]);

  // ===== UTILITY FUNCTIONS =====
  const handleAddKYC = () => setKycModalOpen(true);
  const handleAddBankDetails = () => setBankModalOpen(true);
  const handleAddTransaction = () => setTransactionModalOpen(true);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    try {
      return format(parseISO(dateString), "dd/MM/yyyy HH:mm:ss");
    } catch (e) {
      return dateString;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'success':
        return <VerifiedIcon sx={{ fontSize: 16 }} />;
      case 'pending':
        return <PendingIcon sx={{ fontSize: 16 }} />;
      default:
        return <ErrorIcon sx={{ fontSize: 16 }} />;
    }
  };

  const getCompletionPercentage = () => {
    let completed = 0;
    let total = 3;
    
    if (customer) completed += 1;
    if (kycData.length > 0) completed += 1;
    if (bankData.length > 0) completed += 1;
    
    return (completed / total) * 100;
  };

  // ===== LOADING AND ERROR STATES =====
  if (loading) {
    return <ProfileSkeleton />;
  }

  if (!customer) {
    return (
      <EmptyState
        icon={<ErrorIcon />}
        title="Customer not found"
        description="The requested customer profile could not be loaded."
        action={
          <Button variant="outlined" onClick={() => window.location.reload()}>
            Refresh Page
          </Button>
        }
      />
    );
  }


  const renderProfileHeader = () => (
    <ProfileHeaderCard elevation={0}>
        <Box>
        <Typography
              sx={{
                fontFamily: `"MiSans", "Segoe UI", "Roboto", "Helvetica Neue", sans-serif`,
                textTransform: "capitalize",
                fontWeight: 600,
                letterSpacing: "0.5px",
                color: 'text.primary',
                // textAlign: "center",
                fontSize: "25px",
              }}
            >
                {customer.name || customer.email.split('@')[0]} 
              </Typography>
              
           
            </Box>
    </ProfileHeaderCard>
  );



  // Enhanced Profile Information Card
  const renderProfileCard = () => (
    <InfoCard>
      <Stack spacing={0}>
        {[
          { icon: <EmailIcon />, label: "Email Address", value: customer.email },
          { icon: <PhoneIcon />, label: "Mobile Number", value: `+91 ${customer.mobile}` },
          { icon: <FingerprintIcon />, label: "User ID", value: customer.user_id },
          { icon: <BusinessIcon />, label: "Client ID", value: customer.client_id },
          { icon: <CalendarTodayIcon />, label: "Member Since", value: formatDate(customer.created_at) },
          { icon: <UpdateIcon />, label: "Last Updated", value: formatDate(customer.updated_at) },
        ].map((item, index) => (
          <DataRow key={index}>
            <Box className="data-icon" sx={{ color: 'primary.main', mr: 2, minWidth: 24 }}>
              {item.icon}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography className="data-label" variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                {item.label}
              </Typography>
              <Typography className="data-value" variant="body1" fontWeight="500" sx={{ fontSize: '0.95rem' }}>
                {item.value}
              </Typography>
            </Box>
          </DataRow>
        ))}
      </Stack>
    </InfoCard>
  );

  // Enhanced KYC Card
  const renderKycCard = () => (
    <InfoCard>
      <SectionHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon className="card-icon" color="primary" />
          <Typography variant="h6" fontWeight="bold">
            KYC Verification
          </Typography>
        </Box>
        {kycData.length === 0 && (
          <Tooltip title="Add KYC Documents">
            <IconButton
              onClick={handleAddKYC}
              disabled={!!customer.pid}
              color="primary"
              sx={{
                bgcolor: 'primary.light',
                '&:hover': { bgcolor: 'primary.main', color: 'white' },
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        )}
      </SectionHeader>

      {kycData.length > 0 ? (
        <Stack spacing={3}>
          {["pid", "tid"].map((docType) => {
            const documents = kycData.filter((kyc) => kyc.document_type === docType);
            if (documents.length === 0) return null;
            
            return (
              <Box key={docType}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <DocumentScannerIcon color="primary" sx={{ fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    {docType.toUpperCase()} Documents
                  </Typography>
                </Box>
                
                {documents.map((kyc, index) => (
                  <Paper key={kyc.kyc_id} sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {kyc.document_name.toUpperCase()}
                        </Typography>
                        <StatusChip 
                          label={kyc.kyc_status} 
                          $status={kyc.kyc_status}
                          icon={getStatusIcon(kyc.kyc_status)}
                          size="small"
                        />
                      </Box>
                      
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Document Number</Typography>
                          <Typography variant="body2" fontWeight="500">{kyc.document_number}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Full Name</Typography>
                          <Typography variant="body2" fontWeight="500">{kyc.full_name}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                          <Typography variant="body2" fontWeight="500">{formatDate(kyc.dob)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">Country</Typography>
                          <Typography variant="body2" fontWeight="500">{kyc.country}</Typography>
                        </Grid>
                      </Grid>
                      
                      {kyc.verified_by && (
                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary">
                            Verified by: <strong>{kyc.verified_by.toUpperCase()}</strong> on {formatDate(kyc.created_at)}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                ))}
              </Box>
            );
          })}
        </Stack>
      ) : (
        <EmptyState
          icon={<SecurityIcon />}
          title="No KYC Information"
          description="Add KYC documents to verify customer identity"
          action={
            <Button
              variant="outlined"
              onClick={handleAddKYC}
              disabled={!!customer.pid}
              startIcon={<AddIcon />}
            >
              {customer.pid ? "KYC Added" : "Add KYC Documents"}
            </Button>
          }
        />
      )}
    </InfoCard>
  );

  // Enhanced Bank Card
  const renderBankCard = () => (
    <InfoCard>
      <SectionHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalanceIcon className="card-icon" color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Banking Information
          </Typography>
        </Box>
        <Tooltip title="Add Bank Details">
          <IconButton
            onClick={handleAddBankDetails}
            disabled={!!customer.bank_id}
            color="primary"
            sx={{
              bgcolor: 'primary.light',
              '&:hover': { bgcolor: 'primary.main', color: 'white' },
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      </SectionHeader>

      {bankData.length > 0 ? (
        <Stack spacing={2}>
          {bankData.map((bank, index) => (
            <Paper key={bank.bank_id} sx={{ p: 3, bgcolor: 'background.default' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CreditCardIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    {bank.bank_name || "Primary Bank Account"}
                  </Typography>
                </Box>
                <StatusChip 
                  label={bank.bank_status} 
                  $status={bank.bank_status}
                  icon={getStatusIcon(bank.bank_status)}
                />
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Account Number</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                    {bank.account_number}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">IFSC Code</Typography>
                  <Typography variant="h6" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                    {bank.ifsc_code}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Bank ID</Typography>
                  <Typography variant="body2" fontWeight="500">{bank.bank_id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Added On</Typography>
                  <Typography variant="body2" fontWeight="500">{formatDate(bank.created_at)}</Typography>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Stack>
      ) : (
        <EmptyState
          icon={<AccountBalanceIcon />}
          title="No Banking Information"
          description="Add bank details to enable transactions"
          action={
            <Button
              variant="outlined"
              onClick={handleAddBankDetails}
              disabled={!!customer.bank_id}
              startIcon={<AddIcon />}
            >
              {customer.bank_id ? "Bank Details Added" : "Add Bank Details"}
            </Button>
          }
        />
      )}
    </InfoCard>
  );

  // Enhanced Transaction Section
  const renderTransactionSection = () => (
    <InfoCard sx={{ mt: 3 }}>
      <SectionHeader>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon className="card-icon" color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Transaction History
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={`${transactions.length} Transactions`} 
            color="primary" 
            variant="outlined"
            size="small"
          />
          <Tooltip title="Create New Transaction">
            <IconButton
              onClick={handleAddTransaction}
              color="primary"
              sx={{
                bgcolor: 'primary.light',
                '&:hover': { bgcolor: 'primary.main', color: 'white' },
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </SectionHeader>

      {transactionsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : transactions.length > 0 ? (
        <Stack spacing={2}>
          {transactions.map((transaction) => (
            <Accordion
              key={transaction.transaction_id}
              sx={{
                '&:before': { display: 'none' },
                borderRadius: '12px !important',
                border: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: 2,
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  bgcolor: 'background.default',
                  borderRadius: '12px',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {transaction.transaction_type === 'credit' ? 
                        <TrendingUpIcon color="success" /> : 
                        <TrendingDownIcon color="error" />
                      }
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Transaction ID
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                          {transaction.transaction_id.slice(0, 8)}...
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant={isMobile ? "body1" : "h6"} fontWeight="bold" color="primary">
                      {transaction.currency_code} {parseFloat(transaction.amount).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: isMobile ? 'flex-start' : 'flex-end', flexWrap: 'wrap' }}>
                      <StatusChip
                        label={transaction.transaction_type}
                        $status={transaction.transaction_type === 'credit' ? 'success' : 'error'}
                        size="small"
                      />
                      <StatusChip
                        label={transaction.transaction_status}
                        $status={transaction.transaction_status}
                        size="small"
                      />
                    </Box>
                  </Grid>
                </Grid>
              </AccordionSummary>
              
              <AccordionDetails sx={{ bgcolor: 'background.paper' }}>
                <Stack spacing={3}>
                  {/* Amount Highlight */}
                  <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                    <Typography variant={isMobile ? "h4" : "h3"} fontWeight="bold">
                      {transaction.currency_code} {parseFloat(transaction.amount).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {transaction.transaction_type.toUpperCase()} Transaction
                    </Typography>
                  </Paper>

                  {/* User Details */}
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          From User
                        </Typography>
                        <Stack spacing={1}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {transaction.from_user.email}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {transaction.from_user.mobile}
                          </Typography>
                          <Chip 
                            label={transaction.from_user.user_type} 
                            size="small" 
                            variant="outlined"
                          />
                        </Stack>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          To User
                        </Typography>
                        <Stack spacing={1}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {transaction.to_user.email}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {transaction.to_user.mobile}
                          </Typography>
                          <Chip 
                            label={transaction.to_user.user_type} 
                            size="small" 
                            variant="outlined"
                          />
                        </Stack>
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Bank Details */}
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Bank Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Typography variant="caption" color="text.secondary">Account Number</Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                          {transaction.benif_bank.account_number}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="caption" color="text.secondary">IFSC Code</Typography>
                        <Typography variant="body1" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
                          {transaction.benif_bank.ifsc_code}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="caption" color="text.secondary">Bank Status</Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <StatusChip
                            label={transaction.benif_bank.bank_status}
                            $status={transaction.benif_bank.bank_status}
                            size="small"
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Timeline */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    pt: 2, 
                    borderTop: '1px solid', 
                    borderColor: 'divider',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 2 : 0
                  }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Created</Typography>
                      <Typography variant="body2" fontWeight="500">
                        {format(new Date(transaction.created_at), 'dd/MM/yyyy HH:mm')}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: isMobile ? 'left' : 'right' }}>
                      <Typography variant="caption" color="text.secondary">Updated</Typography>
                      <Typography variant="body2" fontWeight="500">
                        {format(new Date(transaction.updated_at), 'dd/MM/yyyy HH:mm')}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      ) : (
        <EmptyState
          icon={<TrendingUpIcon />}
          title="No Transactions Available"
          description="Create your first transaction to get started with financial activities"
          action={
            <Button
              variant="contained"
              onClick={handleAddTransaction}
              startIcon={<AddIcon />}
              size="large"
            >
              Create Transaction
            </Button>
          }
        />
      )}
    </InfoCard>
  );

  // ===== MAIN RENDER =====
  return (
    <>
      <Seo title="Customer Profile" />
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          py: 3, 
          backgroundColor: 'background.default',
          minHeight: '100vh'
        }}
      >

          <Box>
              <BackButton fallbackPath="/dashboard/customers/list" />
            </Box>
        <ResponsiveContainer maxWidth="xl">
          <Stack spacing={4}>
            {/* Back Button */}
        
            {/* Main Content Grid */}
            <Grid container spacing={3}>
              <Grid item xs={12} lg={4}>
                {renderProfileCard()}
              </Grid>
              <Grid item xs={12} lg={4}>
                {renderKycCard()}
              </Grid>
              <Grid item xs={12} lg={4}>
                {renderBankCard()}
              </Grid>
            </Grid>
            
            {/* Transaction Section */}
            {renderTransactionSection()}
          </Stack>
        </ResponsiveContainer>
      </Box>
      
      {/* Scroll to Top Button */}
      <Zoom in={showScrollTop}>
        <Fab
          onClick={scrollToTop}
          color="primary"
          size="medium"
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1000,
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Zoom>
      
      {/* Modals */}
      {id && (
        <>
          <CreateKycModal
            open={kycModalOpen}
            onClose={() => setKycModalOpen(false)}
            onSuccess={() => {
              setKycModalOpen(false);
              fetchKycData();
            }}
            userId={id}
          />
          <CreateBankModal
            open={bankModalOpen}
            onClose={() => setBankModalOpen(false)}
            onSuccess={() => {
              setBankModalOpen(false);
              fetchBankData();
            }}
            userId={id}
          />
          <CreateTransactionModal
            open={transactionModalOpen}
            onClose={() => setTransactionModalOpen(false)}
            onSuccess={() => {
              setTransactionModalOpen(false);
              fetchTransactions();
            }}
            userId={id}
          />
        </>
      )}
    </>
  );
};

export default CustomerProfilePage;

